import { PapiRequestDaBrowseFaceted, PapiTopic } from '@wix/da-types-papi';
import { BIEventPayload } from '@wix/da-shared-react/pkg/biLogger';
import { Topic } from '@wix/da-shared-react/pkg/types/topics';
import {
  getIsMobile,
  getIsLoggedIn,
} from '@wix/da-shared-react/pkg/publicSession';
import {
  Pages,
  BrowseLayout,
  ErrorNames,
  PAGE_STREAM_ID,
} from '../../constants';
import {
  OrderOption,
  FacetPageType,
  FilterOption,
  Strip,
} from '../../types/api';
import { AppState } from '../types/store';
import { getPage, getThisTopic, getCurrentPageType } from './page';
import { flagEnabled } from '@wix/da-shared-react/pkg/redux/flags';
import { selectors as streamSelectors } from '@wix/da-shared-react/pkg/Stream';
import { getStreamItems } from './stream';
import { getIsNetworkBarPage } from './app';

function getFacet(state: AppState): AppState['facet'] {
  return state.facet;
}

export function getStrips(state: AppState) {
  return (
    (state.currentPageData?.strips as Strip[]) ??
    (getFacet(state).strips as Strip[])
  );
}

export function getTopStrips(state: AppState): Strip[] {
  return (
    (state.currentPageData?.topStrips as Strip[]) ??
    (getFacet(state)?.topStrips as Strip[]) ??
    []
  );
}

export function getTopTags(state: AppState) {
  return getFacet(state).topTags;
}

export function getMainContentUrl(state: AppState) {
  return getFacet(state).mainContentUrl;
}

export function getFilterTags(state: AppState) {
  return getFacet(state).filterTags;
}

export function getSelectedTags(state: AppState) {
  return getFacet(state).selectedTags;
}

export function getEstimatedGrandTotal(state: AppState) {
  return getFacet(state).estTotalTotal;
}

export function getPageBiMetadata(state: AppState) {
  const facet = getFacet(state);
  return (
    state.currentPageData?.biMetaData ?? (facet ? facet.pageBiMetadata : '')
  );
}

export function getEffectiveOrder(state: AppState): string {
  return getFacet(state).effectiveOrder || '';
}

export function getOrderOptions(state: AppState): OrderOption[] {
  return getFacet(state).orderOptions || [];
}

export function getContentTypeFilterOptions(state: AppState): FilterOption[] {
  return getFacet(state).contentTypeFilterOptions ?? [];
}

export function getActiveContentTypeFilter(state: AppState) {
  return getFacetRequestQuery(state).content_type_filter;
}

export function getFacetRequestQueryForPage(
  state: AppState,
  page: FacetPageType
): PapiRequestDaBrowseFaceted {
  return (
    state.requestParams[page] || {
      page_type: page,
    }
  );
}

export function getFacetRequestQuery(
  state: AppState
): PapiRequestDaBrowseFaceted {
  return getFacetRequestQueryForPage(state, state.currentFacetPage);
}

export function getRequestPageType(state: AppState) {
  return getFacetRequestQuery(state).page_type;
}

export function getEffectivePageType(state: AppState): FacetPageType {
  return (
    (getFacet(state)?.effectivePageType as FacetPageType) ??
    getRequestPageType(state)
  );
}

export function getActiveFacets(state: AppState) {
  const { facet_category, facet_type, facet_content } = getFacetRequestQuery(
    state
  );
  return {
    facet_category,
    facet_type,
    facet_content,
  };
}

export function getFacetPageType(state: AppState): FacetPageType | undefined {
  const page = getPage(state);

  if (page === Pages.Tag) {
    return 'tag';
  }

  return getRequestPageType(state);
}

export function getSearchTerm(state: AppState): string | undefined {
  return getFacetRequestQuery(state).q;
}

export function getFacetPageMenu(state: AppState) {
  return state.pageData.otherPageTypes;
}

const errorCodeMap = {
  0: ErrorNames.None,
  1: ErrorNames.Request,
  2: ErrorNames.HighOffset,
  3: ErrorNames.NoResults,
  4: ErrorNames.PastEndOfResults,
  5: ErrorNames.AllMature,
  6: ErrorNames.AllStored,
  7: ErrorNames.AllDeleted,
  8: ErrorNames.AllAntisocial,
  9: ErrorNames.HighLoad,
  10: ErrorNames.AllBlocked,
  11: ErrorNames.NoDeviantsYouWatch,
  742: ErrorNames.TagBlocked,
};

export function getErrorName(state: AppState): ErrorNames {
  const errorCode =
    state.currentPageData?.errorCode ?? getFacet(state).errorCode;

  if (errorCode === undefined) {
    return ErrorNames.None;
  }

  if (
    !flagEnabled(state, 'empty_because_not_watching_anyone') &&
    errorCode === 11
  ) {
    return ErrorNames.NoResults;
  }

  return errorCodeMap[errorCode] ?? ErrorNames.None;
}

export function getDailyDeviationDate(state: AppState) {
  if (getIsNetworkBarPage(state)) {
    return streamSelectors.getStreamParam(
      state,
      PAGE_STREAM_ID,
      'requestParams'
    )?.date;
  }

  return getFacetRequestQuery(state).q;
}

export function getTopic(state: AppState): Topic | undefined {
  return getThisTopic(state) ?? (getFacet(state) || {}).neco;
}

export function isSingleTopicPage(state: AppState): boolean {
  const pageType = getFacetPageType(state);
  return pageType === 'topics' && Boolean(getTopic(state));
}

export type TypeAndItemIds = Pick<BIEventPayload, 'typeid' | 'itemid'>;
export function getTopicTypeAndItemIds(state: AppState): TypeAndItemIds {
  const topic = getTopic(state);
  return topic ? { typeid: topic.typeId, itemid: topic.itemId } : {};
}

export function getLayout(state: AppState): BrowseLayout {
  if (getIsNetworkBarPage(state)) {
    const layout = state.currentPageData?.layout ?? BrowseLayout.Torpedo;
    if (getIsMobile(state) && layout === BrowseLayout.Torpedo) {
      return BrowseLayout.Grid;
    }
    return layout;
  }

  if (getPage(state) === Pages.Posts) {
    return BrowseLayout.Feed;
  }

  const layout = getFacet(state).layout ?? BrowseLayout.Grid;

  // replace torpedo with grid in mobile
  if (getIsMobile(state) && layout === BrowseLayout.Torpedo) {
    return BrowseLayout.Grid;
  }

  return layout;
}

export function getTopTopics(state: AppState) {
  return getFacet(state).topNecos ?? [];
}

export function getAllTopics(state: AppState) {
  if (getIsNetworkBarPage(state)) {
    return (getStreamItems(state, PAGE_STREAM_ID) as unknown) as PapiTopic[];
  }

  return getFacet(state).allNecos ?? [];
}

export function getCanShowLessLikeThis(state: AppState) {
  if (getIsNetworkBarPage(state)) {
    return getCurrentPageType(state) === 'home' && getIsLoggedIn(state);
  }

  return getEffectiveOrder(state) === 'recommended';
}
